import { inject, Injectable, Injector } from '@angular/core';
import { Client } from '../interface/client';
import { ClientService } from './client.service';
import { Company } from '../interface/company';
import Dexie, { Table } from 'dexie';
import moment, { Moment } from 'moment';
import { ClientUtilities } from '../class/client-utilities';
import { Utilities } from '../class/utilities';
import { BehaviorSubject } from 'rxjs';

type ClientCursor = string | number;

@Injectable({
  providedIn: 'root'
})
export class ClientIndexedDbService extends Dexie {
  private injector = inject(Injector);
  private _clientService!: ClientService;
  private _clientsDb!: Table<Client, string>;
  private _lastGetAll: Moment;

  syncing$ = new BehaviorSubject<boolean>(false);

  constructor() {
    super('clients');

    this.version(1).stores({
      clients: 'id, name, document, phone1, phone2, fancyName'
    });

    this._clientsDb = this.table('clients');
  }

  async localStoreClients(company: Company) {
    this._clientsDb.clear();
    this.lastGetAll = moment();
    this.syncing$.next(true);
    const _startSync = Date.now();
    console.log('[IndexedDB] localStoreClients START');

    try {
      const allClients = await this.clientService.getAll(false, company.cnpjMatrix ?? company.cnpj);
      console.log(`[IndexedDB] API returned ${allClients.length} clients (${Date.now() - _startSync}ms)`);

      const validClients = allClients.filter(
        client => !ClientUtilities.isDefault(client.document)
      );

      // Insert in chunks to avoid blocking the UI thread
      const CHUNK_SIZE = 2000;
      let inserted = 0;
      for (let i = 0; i < validClients.length; i += CHUNK_SIZE) {
        const chunk = validClients.slice(i, i + CHUNK_SIZE).map(client => this.normalizeForDb(client));
        try {
          await this._clientsDb.bulkPut(chunk);
          inserted += chunk.length;
        } catch (error) {
          console.warn(`[IndexedDB] Failed to insert chunk at index ${i}`, error);
        }
      }

      console.log(`[IndexedDB] Inserted ${inserted} clients`);
    } catch (error) {
      console.error(error);
    } finally {
      this.syncing$.next(false);
      console.log(`[IndexedDB] localStoreClients END (${Date.now() - _startSync}ms)`);
    }
  }

  private get clientService(): ClientService {
    if (!this._clientService) {
      this._clientService = this.injector.get(ClientService);
    }
    return this._clientService;
  }

  private normalizeSearchText(text: string): string {
    const normalized = Utilities.removeDiacritics(text || '').toLowerCase().replace(/[.\-/]/g, '').trim();
    return normalized.replace(/\s+/g, ' ');
  }

  private async queryPrefix(indexName: keyof Client, prefix: string, limit?: number): Promise<Client[]> {
    const collection = this._clientsDb.where(indexName as string).startsWithIgnoreCase(prefix);
    return limit ? collection.limit(limit).toArray() : collection.toArray();
  }

  private dedupeClients(clients: Client[]): Client[] {
    const map = new Map<string, Client>();
    clients.forEach(client => {
      if (client?.id && !map.has(client.id)) {
        map.set(client.id, client);
      }
    });
    return Array.from(map.values());
  }

  private normalizeForDb(client: Client): Client {
    return client;
  }

  private async searchByPrefix(prefix: string, limit?: number, pageIndex = 0): Promise<{ clients: Client[], hasNext: boolean }> {
    const pageSize = limit ? (pageIndex + 1) * limit + 1 : undefined;
    const queries = [
      this.queryPrefix('name', prefix, pageSize),
      this.queryPrefix('fancyName', prefix, pageSize),
      this.queryPrefix('document', prefix, pageSize),
      this.queryPrefix('phone1', prefix, pageSize),
      this.queryPrefix('phone2', prefix, pageSize)
    ];

    const results = await Promise.all(queries);
    const merged = this.dedupeClients(results.flat());
    const hasNext = limit ? merged.length > (pageIndex + 1) * limit : false;
    const paged = limit ? merged.slice(pageIndex * limit, pageIndex * limit + limit) : merged;
    return { clients: paged, hasNext };
  }

  async addClient(client: Client) {
    const isDefaltClient = ClientUtilities.isDefault(client.document);

    if (!isDefaltClient) {
      await this._clientsDb.put(this.normalizeForDb(client));
    } else {
      await this._clientsDb.delete(client.id);
    };
  }

  async updateClient(client: Client) {
    const isDefaltClient = ClientUtilities.isDefault(client.document);

    if (!isDefaltClient) {
      await this._clientsDb.put(this.normalizeForDb(client));
    };
  }

  async getById(id: string) {
    return await this._clientsDb.get(id);
  }

  private matchesSearch(client: Client, textToSearch: string): boolean {
    const fields = [client.name, client.document, client.phone1, client.phone2, client.fancyName];
    return fields.some(field =>
      Utilities.removeDiacritics(field || '').toLowerCase().includes(textToSearch)
    );
  }

  async searchForUsers(text: string): Promise<Client[]> {
    const searchToken = this.normalizeSearchText(text);
    if (!searchToken) {
      return await this._clientsDb.orderBy('id').limit(20).toArray();
    }

    return (await this.searchByPrefix(searchToken, 50, 0)).clients;
  }

  private toCursor(client: Client): ClientCursor {
    return client.id;
  }

  async countSearchResults(text: string): Promise<number> {
    const searchToken = this.normalizeSearchText(text);
    if (!searchToken) {
      return await this._clientsDb.count();
    }

    const { clients } = await this.searchByPrefix(searchToken);
    return clients.length;
  }

  private async searchPaginatedByCursor(textToSearch: string | undefined, limit: number, after?: ClientCursor): Promise<{ clients: Client[], nextCursor?: ClientCursor, hasNext: boolean }> {
    if (!textToSearch) {
      const collection = after
        ? this._clientsDb.where('id').above(after as string)
        : this._clientsDb.orderBy('id');

      const pageItems = await collection.limit(limit + 1).toArray();
      const hasNext = pageItems.length > limit;
      if (hasNext) {
        pageItems.pop();
      }

      const nextCursor = pageItems.length > 0 ? this.toCursor(pageItems[pageItems.length - 1]) : undefined;
      return { clients: pageItems, nextCursor, hasNext };
    }

    const pageIndex = typeof after === 'number' ? after : 0;
    const { clients, hasNext } = await this.searchByPrefix(textToSearch, limit + 1, pageIndex);
    const nextCursor = hasNext ? pageIndex + 1 : undefined;

    return { clients, nextCursor, hasNext };
  }

  set lastGetAll(date: Moment) {
    this._lastGetAll = date;
  }

  get lastGetAll() {
    return this._lastGetAll;
  }

  async searchPaginated(text: string, limit: number, after?: ClientCursor): Promise<{ clients: Client[], nextCursor?: ClientCursor, hasNext: boolean }> {
    const _start = Date.now();
    const textToSearch = this.normalizeSearchText(text);
    const result = await this.searchPaginatedByCursor(textToSearch, limit, after);
    console.log(`[IndexedDB] searchPaginated('${text}', ${limit}, ${after ?? 'start'}) -> ${result.clients.length} (${Date.now() - _start}ms)`);
    return result;
  }
}
